import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  subaccountEndpoint,
  SubaccountDetails,
} from '../../services/api/endpoints/subaccount.endpoint';
import { PurchaseAddress } from '../../types/phone-numbers';
import { logger } from '../../utils/logger';

interface SubaccountState {
  subaccountSid: string | null;
  friendlyName: string | null;
  status: string | null;
  isLoading: boolean;
  isCreating: boolean;
  isDeleting: boolean;
  error: string | null;
}

const initialState: SubaccountState = {
  subaccountSid: null,
  friendlyName: null,
  status: null,
  isLoading: false,
  isCreating: false,
  isDeleting: false,
  error: null,
};

export const useSubaccount = () => {
  const organisationId = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation?.id ?? null
  );
  const [state, setState] = useState<SubaccountState>(initialState);

  const fetchSubaccount = useCallback(async () => {
    if (!organisationId) return;
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const data: SubaccountDetails | null = await subaccountEndpoint.getSubaccount(organisationId);
      setState(prev => ({
        ...prev,
        isLoading: false,
        subaccountSid: data?.twilioSubaccountSid ?? null,
        friendlyName: data?.friendlyName ?? null,
        status: data?.status ?? null,
      }));
    } catch (err) {
      logger.error('useSubaccount: fetchSubaccount failed', err);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch subaccount',
      }));
    }
  }, [organisationId]);

  const createSubaccount = useCallback(
    async (friendlyName?: string, address?: PurchaseAddress) => {
      if (!organisationId) return;
      setState(prev => ({ ...prev, isCreating: true, error: null }));
      try {
        await subaccountEndpoint.createSubaccount(organisationId, friendlyName, address);
        // Re-fetch to get the full subaccount details
        const data = await subaccountEndpoint.getSubaccount(organisationId);
        setState(prev => ({
          ...prev,
          isCreating: false,
          subaccountSid: data?.twilioSubaccountSid ?? null,
          friendlyName: data?.friendlyName ?? null,
          status: data?.status ?? null,
        }));
      } catch (err) {
        logger.error('useSubaccount: createSubaccount failed', err);
        setState(prev => ({
          ...prev,
          isCreating: false,
          error: err instanceof Error ? err.message : 'Failed to create subaccount',
        }));
        throw err;
      }
    },
    [organisationId]
  );

  const deleteSubaccount = useCallback(async () => {
    if (!organisationId) return;
    setState(prev => ({ ...prev, isDeleting: true, error: null }));
    try {
      await subaccountEndpoint.deleteSubaccount(organisationId);
      setState({ ...initialState });
    } catch (err) {
      logger.error('useSubaccount: deleteSubaccount failed', err);
      setState(prev => ({
        ...prev,
        isDeleting: false,
        error: err instanceof Error ? err.message : 'Failed to delete subaccount',
      }));
      throw err;
    }
  }, [organisationId]);

  return {
    ...state,
    hasSubaccount: !!state.subaccountSid,
    fetchSubaccount,
    createSubaccount,
    deleteSubaccount,
  };
};
